/**
 * Utils Service
 * Core utility functions for developer tools
 */
import crypto from 'crypto';
import bcrypt from 'bcryptjs';
import { readFile } from 'fs/promises';

// ============================================
// TYPES
// ============================================

export interface PasswordOptions {
    length: number;
    lowercase: boolean;
    uppercase: boolean;
    digits: boolean;
    symbols: boolean;
    symbolChars?: string;
}

export interface UuidOptions {
    uppercase?: boolean;
    noHyphens?: boolean;
}

export type HashAlgorithm = 'md5' | 'sha1' | 'sha256' | 'sha512' | 'bcrypt';

export interface HttpRequestOptions {
    method: string;
    headers?: Record<string, string>;
    body?: string;
    timeout?: number;
    followRedirects?: boolean;
}

export interface HttpResponse {
    status: number;
    statusText: string;
    headers: Record<string, string>;
    body: string;
    timing: number;
    size: number;
}

// ============================================
// UTILS SERVICE
// ============================================

export class UtilsService {
    /**
     * Generate secure random password
     */
    generatePassword(options: PasswordOptions): string {
        const {
            length,
            lowercase,
            uppercase,
            digits,
            symbols,
            symbolChars = '!@#$%^&*()_+-=[]{}|;:,.<>?'
        } = options;

        let charset = '';
        if (lowercase) charset += 'abcdefghijklmnopqrstuvwxyz';
        if (uppercase) charset += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
        if (digits) charset += '0123456789';
        if (symbols) charset += symbolChars;

        if (charset.length === 0) {
            throw new Error('At least one character type must be enabled');
        }

        const randomBytes = crypto.randomBytes(length);
        let password = '';

        for (let i = 0; i < length; i++) {
            const randomIndex = randomBytes[i] % charset.length;
            password += charset[randomIndex];
        }

        return password;
    }

    /**
     * Generate UUID v4
     */
    generateUuid(options?: UuidOptions): string {
        let uuid = crypto.randomUUID();

        if (options?.uppercase) {
            uuid = uuid.toUpperCase();
        }

        if (options?.noHyphens) {
            uuid = uuid.replace(/-/g, '');
        }

        return uuid;
    }

    /**
     * Hash text or file using specified algorithm
     */
    async hash(input: string | Buffer, algorithm: HashAlgorithm): Promise<string> {
        if (algorithm === 'bcrypt') {
            throw new Error('Use hashBcrypt for bcrypt algorithm');
        }

        const hash = crypto.createHash(algorithm);
        hash.update(input);
        return hash.digest('hex');
    }

    /**
     * Hash file contents
     */
    async hashFile(filePath: string, algorithm: HashAlgorithm): Promise<string> {
        const content = await readFile(filePath);
        return this.hash(content, algorithm);
    }

    /**
     * Hash using bcrypt
     */
    async hashBcrypt(input: string, rounds: number = 10): Promise<string> {
        return bcrypt.hash(input, rounds);
    }

    /**
     * Base64 encode
     */
    base64Encode(input: string | Buffer, urlSafe: boolean = false): string {
        const buffer = typeof input === 'string' ? Buffer.from(input, 'utf-8') : input;
        let encoded = buffer.toString('base64');

        if (urlSafe) {
            encoded = encoded.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
        }

        return encoded;
    }

    /**
     * Base64 decode
     */
    base64Decode(input: string): Buffer {
        // Handle URL-safe base64
        let normalized = input.replace(/-/g, '+').replace(/_/g, '/');

        // Add padding if needed
        while (normalized.length % 4) {
            normalized += '=';
        }

        return Buffer.from(normalized, 'base64');
    }

    /**
     * Make HTTP request with detailed output
     */
    async httpRequest(url: string, options: HttpRequestOptions): Promise<HttpResponse> {
        const startTime = performance.now();

        const fetchOptions: RequestInit = {
            method: options.method,
            headers: options.headers,
            body: options.body,
            signal: options.timeout ? AbortSignal.timeout(options.timeout) : undefined,
            redirect: options.followRedirects === false ? 'manual' : 'follow',
        };

        const response = await fetch(url, fetchOptions);
        const body = await response.text();
        const endTime = performance.now();

        const headers: Record<string, string> = {};
        response.headers.forEach((value, key) => {
            headers[key] = value;
        });

        return {
            status: response.status,
            statusText: response.statusText,
            headers,
            body,
            timing: Math.round(endTime - startTime),
            size: Buffer.byteLength(body, 'utf-8'),
        };
    }

    /**
     * Decode JWT (no verification)
     */
    jwtDecode(token: string): { header: object; payload: object; signature: string } {
        const parts = token.split('.');
        if (parts.length !== 3) {
            throw new Error('Invalid JWT format');
        }

        const [headerB64, payloadB64, signature] = parts;

        const header = JSON.parse(this.base64Decode(headerB64).toString('utf-8'));
        const payload = JSON.parse(this.base64Decode(payloadB64).toString('utf-8'));

        return { header, payload, signature };
    }

    /**
     * Encode JWT (HS256 only)
     */
    jwtEncode(payload: object, secret: string, header?: object): string {
        const defaultHeader = { alg: 'HS256', typ: 'JWT' };
        const finalHeader = { ...defaultHeader, ...header };

        const headerB64 = this.base64Encode(JSON.stringify(finalHeader), true);
        const payloadB64 = this.base64Encode(JSON.stringify(payload), true);

        const signatureInput = `${headerB64}.${payloadB64}`;
        const signature = crypto
            .createHmac('sha256', secret)
            .update(signatureInput)
            .digest('base64')
            .replace(/\+/g, '-')
            .replace(/\//g, '_')
            .replace(/=/g, '');

        return `${headerB64}.${payloadB64}.${signature}`;
    }

    /**
     * Convert unix timestamp to date
     */
    unixToDate(timestamp: number, milliseconds: boolean = false): Date {
        return new Date(milliseconds ? timestamp : timestamp * 1000);
    }

    /**
     * Convert date to unix timestamp
     */
    dateToUnix(date: Date, milliseconds: boolean = false): number {
        const timestamp = date.getTime();
        return milliseconds ? timestamp : Math.floor(timestamp / 1000);
    }

    /**
     * Convert time between timezones
     */
    convertTimezone(
        time: Date | string,
        fromTimezone: string,
        toTimezone: string
    ): { time: string; iso: string } {
        const date = typeof time === 'string' ? new Date(time) : time;

        // Format in target timezone
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: toTimezone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false,
        });

        const parts = formatter.formatToParts(date);
        const dateParts = parts.reduce((acc, part) => {
            if (part.type !== 'literal') {
                acc[part.type] = part.value;
            }
            return acc;
        }, {} as Record<string, string>);

        const time24 = `${dateParts.year}-${dateParts.month}-${dateParts.day} ${dateParts.hour}:${dateParts.minute}:${dateParts.second}`;

        return {
            time: time24,
            iso: date.toISOString(),
        };
    }

    /**
     * Get list of available timezones
     */
    getTimezones(): string[] {
        // Common timezones
        return [
            'UTC',
            'America/New_York',
            'America/Chicago',
            'America/Denver',
            'America/Los_Angeles',
            'America/Sao_Paulo',
            'Europe/London',
            'Europe/Paris',
            'Europe/Berlin',
            'Europe/Moscow',
            'Asia/Dubai',
            'Asia/Kolkata',
            'Asia/Bangkok',
            'Asia/Shanghai',
            'Asia/Tokyo',
            'Asia/Seoul',
            'Asia/Ho_Chi_Minh',
            'Australia/Sydney',
            'Pacific/Auckland',
        ];
    }

    /**
     * URL encode
     */
    urlEncode(input: string, full: boolean = false): string {
        return full ? encodeURI(input) : encodeURIComponent(input);
    }

    /**
     * URL decode
     */
    urlDecode(input: string, full: boolean = false): string {
        return full ? decodeURI(input) : decodeURIComponent(input);
    }

    /**
     * Format bytes to human-readable size
     */
    formatBytes(bytes: number): string {
        if (bytes === 0) return '0 B';

        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));

        return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
    }
}
